import styled from '@emotion/styled';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import { Fab, Tooltip } from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { PUBLIC_ROUTES } from './public-routes';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import * as GQL from '~/graphql/generated/client';
import { dashboardRouteUtil } from '~c/routes';

const PUBLIC_ROUTE_PREFIXES = Object.values(PUBLIC_ROUTES).map((route) =>
  route.split(':')[0].replace(/\/$/, '')
);

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

const StyledFab = styled(Fab)<{ tokens?: DesignTokenSet }>`
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 1300;
  background-color: ${(props) => (props.tokens ?? DesignToken).status.danger};
  color: #ffffff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
  animation: floatPulse 2.4s ease-in-out infinite;

  &:hover {
    background-color: ${(props) => (props.tokens ?? DesignToken).status.danger};
    transform: scale(1.08);
  }

  @keyframes floatPulse {
    0% {
      box-shadow:
        0 6px 18px rgba(0, 0, 0, 0.28),
        0 0 0 0 ${(props) => (props.tokens ?? DesignToken).status.danger}66;
    }
    70% {
      box-shadow:
        0 6px 18px rgba(0, 0, 0, 0.28),
        0 0 0 12px ${(props) => (props.tokens ?? DesignToken).status.danger}00;
    }
    100% {
      box-shadow:
        0 6px 18px rgba(0, 0, 0, 0.28),
        0 0 0 0 ${(props) => (props.tokens ?? DesignToken).status.danger}00;
    }
  }
`;

/**
 * Global floating action button shown on every authenticated page when the
 * current user has pending tasks to review. Clicking it navigates to the
 * tasks page. Skips the query entirely on public (unauthenticated) routes.
 */
const FloatingTaskButton: React.FC = () => {
  const tokens = useDesignTokens();
  const location = useLocation();
  const { navigateTo } = dashboardRouteUtil.useHelper();
  const shouldSkip = isPublicRoute(location.pathname);

  const { data } = GQL.useHasPendingTasksQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: shouldSkip ? 0 : 30000,
    skip: shouldSkip,
  });

  const hasPendingTasks = Boolean(data?.hasPendingTasks?.hasPendingTasks);

  if (shouldSkip || !hasPendingTasks) {
    return null;
  }

  return (
    <Tooltip title="You have tasks to review" placement="left" arrow>
      <StyledFab
        tokens={tokens}
        aria-label="Review pending tasks"
        onClick={() => navigateTo('TASKS')}
      >
        <AssignmentTurnedInOutlined />
      </StyledFab>
    </Tooltip>
  );
};

export default FloatingTaskButton;
